import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import {
  PlayArrow,
  Stop,
  Refresh,
  Delete,
  Timer,
  Memory,
  Speed,
  Storage,
  NetworkCheck,
  ExpandMore,
  ExpandLess,
  Assessment,
  AccessTime,
} from "@mui/icons-material";
import {
  startApmSession,
  stopApmSession,
  fetchApmSnapshot,
  fetchApmSessions,
  fetchApmSession,
  deleteApmSession,
  clearActiveSession,
  clearSessionDetail,
} from "../redux/slices/apmSlice";
import MonitoringTab from "./MonitoringTab";

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDuration = (ms) => {
  if (!ms) return "0s";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60000);
  const sec = Math.round((ms % 60000) / 1000);
  return `${min}m ${sec}s`;
};

/**
 * APM Tab component - used in SiteDetail and ApplicationDetail
 *
 * Props:
 * - targetType: "site" | "application" | "service"
 * - targetId: the ID of the target
 * - targetName: display name
 */
const ApmTab = ({ targetType, targetId, targetName }) => {
  const dispatch = useDispatch();
  const {
    activeSession,
    liveSnapshot,
    sessionDetail,
    sessions,
    startLoading,
    stopLoading,
    snapshotLoading,
    listLoading,
    error,
  } = useSelector((state) => state.apm);

  const [pollingActive, setPollingActive] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [durationMin, setDurationMin] = useState(5);
  const [remainingMs, setRemainingMs] = useState(null);
  const [subTab, setSubTab] = useState(targetType === "site" ? 0 : 1);
  const pollingRef = useRef(null);
  const countdownRef = useRef(null);

  // Load sessions history on mount
  useEffect(() => {
    dispatch(fetchApmSessions({ targetType, targetId, limit: 10 }));
    return () => {
      dispatch(clearActiveSession());
      dispatch(clearSessionDetail());
    };
  }, [dispatch, targetType, targetId]);

  // Polling for live snapshot when session is active
  useEffect(() => {
    if (activeSession && pollingActive) {
      pollingRef.current = setInterval(() => {
        dispatch(fetchApmSnapshot(activeSession.id)).then((result) => {
          if (result.payload?.autoStopped) {
            // Session auto-stopped on the server
            setPollingActive(false);
            setRemainingMs(0);
            dispatch(clearActiveSession());
            dispatch(fetchApmSessions({ targetType, targetId, limit: 10 }));
          } else if (result.payload?.remainingMs != null) {
            setRemainingMs(result.payload.remainingMs);
          }
        });
      }, 3000);

      // Local countdown timer (updates every second for smooth UX)
      if (activeSession.config?.durationMs) {
        const startedAt = new Date(activeSession.startedAt).getTime();
        const durationTotal = activeSession.config.durationMs;
        countdownRef.current = setInterval(() => {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, durationTotal - elapsed);
          setRemainingMs(remaining);
          if (remaining <= 0) {
            clearInterval(countdownRef.current);
          }
        }, 1000);
      }
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [activeSession, pollingActive, dispatch, targetType, targetId]);

  const handleStart = useCallback(() => {
    dispatch(
      startApmSession({
        targetType,
        targetId,
        name: `Profile - ${targetName}`,
        config: { intervalMs: 5000, durationMin },
      }),
    ).then((result) => {
      if (!result.error) {
        setPollingActive(true);
        if (result.payload?.session?.config?.durationMs) {
          setRemainingMs(result.payload.session.config.durationMs);
        }
      }
    });
  }, [dispatch, targetType, targetId, targetName, durationMin]);

  const handleStop = useCallback(() => {
    if (activeSession) {
      setPollingActive(false);
      setRemainingMs(null);
      dispatch(stopApmSession(activeSession.id)).then(() => {
        dispatch(fetchApmSessions({ targetType, targetId, limit: 10 }));
      });
    }
  }, [dispatch, activeSession, targetType, targetId]);

  const handleViewSession = useCallback(
    (sessionId) => {
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        dispatch(clearSessionDetail());
      } else {
        setExpandedSession(sessionId);
        dispatch(fetchApmSession(sessionId));
      }
    },
    [dispatch, expandedSession],
  );

  const handleDeleteSession = useCallback(
    (sessionId) => {
      dispatch(deleteApmSession(sessionId));
    },
    [dispatch],
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Sub-tabs for sites */}
      {targetType === "site" && (
        <Tabs
          value={subTab}
          onChange={(e, v) => setSubTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Request Traces" />
          <Tab label="Container Profiling" />
        </Tabs>
      )}

      {/* Traces tab (sites only) */}
      {subTab === 0 && targetType === "site" && (
        <MonitoringTab siteId={targetId} />
      )}

      {/* Container Profiling tab (original content) */}
      {(subTab === 1 || targetType !== "site") && (
        <Box>
          {/* Header with Start/Stop */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                On-Demand Performance Profiler
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start a profiling session to collect real-time CPU, memory,
                network, and disk metrics. Sessions auto-stop after the selected
                duration (max 30 min).
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {!activeSession && (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={durationMin}
                    label="Duration"
                    onChange={(e) => setDurationMin(e.target.value)}
                  >
                    <MenuItem value={5}>5 minutes</MenuItem>
                    <MenuItem value={10}>10 minutes</MenuItem>
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={20}>20 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                  </Select>
                </FormControl>
              )}
              {!activeSession ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    startLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PlayArrow />
                    )
                  }
                  onClick={handleStart}
                  disabled={startLoading}
                  size="large"
                >
                  Start
                </Button>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {remainingMs != null && remainingMs > 0 && (
                    <Chip
                      icon={<AccessTime />}
                      label={`${formatDuration(remainingMs)} remaining`}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={
                      stopLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Stop />
                      )
                    }
                    onClick={handleStop}
                    disabled={stopLoading}
                    size="large"
                  >
                    Stop
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Active Session - Live View */}
          {activeSession && (
            <Card
              sx={{ mb: 3, border: "2px solid", borderColor: "success.main" }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label="RECORDING"
                      color="success"
                      size="small"
                      sx={{ animation: "pulse 2s infinite" }}
                    />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Live Profiling
                    </Typography>
                    {liveSnapshot && (
                      <Typography variant="body2" color="text.secondary">
                        {liveSnapshot.collectedPoints} samples &middot;{" "}
                        {formatDuration(liveSnapshot.elapsedMs)}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Refresh snapshot">
                    <IconButton
                      size="small"
                      onClick={() =>
                        dispatch(fetchApmSnapshot(activeSession.id))
                      }
                      disabled={snapshotLoading}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Live Metrics Grid */}
                {liveSnapshot?.snapshot ? (
                  <Grid container spacing={2}>
                    {Object.entries(liveSnapshot.snapshot).map(
                      ([container, stats]) => (
                        <Grid item xs={12} md={6} lg={4} key={container}>
                          <Card variant="outlined">
                            <CardContent
                              sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: "monospace" }}
                              >
                                {container}
                              </Typography>
                              {stats.error ? (
                                <Typography color="error" variant="body2">
                                  {stats.error}
                                </Typography>
                              ) : (
                                <Box sx={{ mt: 1 }}>
                                  {/* CPU */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Speed
                                      sx={{
                                        fontSize: 16,
                                        color: "primary.main",
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 40 }}
                                    >
                                      CPU
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(stats.cpu || 0, 100)}
                                      sx={{
                                        flexGrow: 1,
                                        height: 8,
                                        borderRadius: 1,
                                      }}
                                      color={
                                        stats.cpu > 80
                                          ? "error"
                                          : stats.cpu > 50
                                            ? "warning"
                                            : "primary"
                                      }
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 50, textAlign: "right" }}
                                    >
                                      {stats.cpu?.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                  {/* Memory */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Memory
                                      sx={{
                                        fontSize: 16,
                                        color: "secondary.main",
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 40 }}
                                    >
                                      RAM
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(
                                        stats.memory?.percent || 0,
                                        100,
                                      )}
                                      sx={{
                                        flexGrow: 1,
                                        height: 8,
                                        borderRadius: 1,
                                      }}
                                      color={
                                        stats.memory?.percent > 80
                                          ? "error"
                                          : stats.memory?.percent > 50
                                            ? "warning"
                                            : "secondary"
                                      }
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 50, textAlign: "right" }}
                                    >
                                      {formatBytes(stats.memory?.usage)}
                                    </Typography>
                                  </Box>
                                  {/* Network */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <NetworkCheck
                                      sx={{ fontSize: 16, color: "info.main" }}
                                    />
                                    <Typography variant="body2">
                                      Net: ↓
                                      {formatBytes(stats.network?.rxBytes)} ↑
                                      {formatBytes(stats.network?.txBytes)}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ),
                    )}
                  </Grid>
                ) : snapshotLoading ? (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Collecting initial metrics...
                    </Typography>
                  </Box>
                ) : null}

                {/* Application-Level Metrics (HTTP, MySQL, PHP) */}
                {liveSnapshot?.appMetrics && (
                  <AppMetricsPanel appMetrics={liveSnapshot.appMetrics} live />
                )}
              </CardContent>
            </Card>
          )}

          {/* Session History */}
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6">Session History</Typography>
              <IconButton
                size="small"
                onClick={() =>
                  dispatch(
                    fetchApmSessions({ targetType, targetId, limit: 10 }),
                  )
                }
              >
                <Refresh />
              </IconButton>
            </Box>

            {listLoading && <LinearProgress sx={{ mb: 2 }} />}

            {sessions.length === 0 && !listLoading && (
              <Alert severity="info">
                No profiling sessions yet. Click &quot;Start&quot; to begin
                collecting performance data.
              </Alert>
            )}

            {sessions.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Session</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Peak CPU</TableCell>
                      <TableCell>Peak Memory</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <React.Fragment key={session.id}>
                        <TableRow
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleViewSession(session.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {session.name || session.targetName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={session.status}
                              size="small"
                              color={
                                session.status === "COMPLETED"
                                  ? "success"
                                  : session.status === "ACTIVE"
                                    ? "primary"
                                    : session.status === "FAILED"
                                      ? "error"
                                      : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(session.startedAt).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.stoppedAt
                                ? formatDuration(
                                    new Date(session.stoppedAt) -
                                      new Date(session.startedAt),
                                  )
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.summary?.containers
                                ? Math.max(
                                    ...Object.values(
                                      session.summary.containers,
                                    ).map((c) => c.cpu?.max || 0),
                                  ).toFixed(1) + "%"
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.summary?.containers
                                ? formatBytes(
                                    Math.max(
                                      ...Object.values(
                                        session.summary.containers,
                                      ).map(
                                        (c) => c.memory?.maxUsageBytes || 0,
                                      ),
                                    ),
                                  )
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="View details">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewSession(session.id);
                                  }}
                                >
                                  {expandedSession === session.id ? (
                                    <ExpandLess />
                                  ) : (
                                    <ExpandMore />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {session.status !== "ACTIVE" && (
                                <Tooltip title="Delete session">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSession(session.id);
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded session details */}
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{
                              py: 0,
                              borderBottom:
                                expandedSession === session.id
                                  ? undefined
                                  : "none",
                            }}
                          >
                            <Collapse in={expandedSession === session.id}>
                              <SessionDetailView
                                sessionId={session.id}
                                sessionDetail={sessionDetail}
                                loading={
                                  expandedSession === session.id &&
                                  !sessionDetail
                                }
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Pulse animation for the recording chip */}
          <style>
            {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.6; }
            100% { opacity: 1; }
          }
        `}
          </style>
        </Box>
      )}
    </Box>
  );
};

/**
 * Application-Level Metrics Panel - shows HTTP, MySQL, PHP-FPM metrics
 */
const AppMetricsPanel = ({ appMetrics, live }) => {
  if (!appMetrics) return null;

  const { http, mysql, php } = appMetrics;

  return (
    <Box sx={{ mt: 2 }}>
      {!live && (
        <Divider sx={{ mb: 2 }}>
          <Chip label="Application Performance" size="small" />
        </Divider>
      )}
      {live && (
        <Typography
          variant="subtitle2"
          sx={{ mt: 2, mb: 1 }}
          color="text.secondary"
        >
          Application Metrics
        </Typography>
      )}

      <Grid container spacing={2}>
        {/* HTTP Performance */}
        {http && (
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <NetworkCheck sx={{ fontSize: 16, color: "info.main" }} />
                  <Typography variant="subtitle2">HTTP Performance</Typography>
                </Box>
                <Stack spacing={0.5}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Requests
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {http.totalRequests}
                    </Typography>
                  </Box>
                  {http.responseTime && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Avg Response
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {http.responseTime.avg}ms
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          P95 Response
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color={
                            http.responseTime.p95 > 2000
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {http.responseTime.p95}ms
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          P99 Response
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color={
                            http.responseTime.p99 > 5000
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {http.responseTime.p99}ms
                        </Typography>
                      </Box>
                    </>
                  )}
                  {http.errorRate && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          4xx Error Rate
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color={
                            http.errorRate.pct4xx > 5
                              ? "warning.main"
                              : "text.primary"
                          }
                        >
                          {http.errorRate.pct4xx}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          5xx Error Rate
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color={
                            http.errorRate.pct5xx > 1
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {http.errorRate.pct5xx}%
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* MySQL Performance */}
        {mysql && (
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Storage sx={{ fontSize: 16, color: "warning.main" }} />
                  <Typography variant="subtitle2">Database</Typography>
                </Box>
                <Stack spacing={0.5}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Queries/sec
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {mysql.queriesPerSecond}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Slow Queries
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color={
                        mysql.slowQueries > 0 ? "warning.main" : "text.primary"
                      }
                    >
                      {mysql.slowQueries}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Connections
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {mysql.connections?.current || 0} /{" "}
                      {mysql.connections?.max || 151}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Buffer Pool Hit Rate
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color={
                        mysql.bufferPoolHitRate < 95
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {mysql.bufferPoolHitRate}%
                    </Typography>
                  </Box>
                  {mysql.locksWaited > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Lock Waits
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="error.main"
                      >
                        {mysql.locksWaited}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* PHP-FPM Performance */}
        {php && (
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Speed sx={{ fontSize: 16, color: "secondary.main" }} />
                  <Typography variant="subtitle2">PHP Workers</Typography>
                </Box>
                <Stack spacing={0.5}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Peak Active
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {php.peakActiveProcesses} / {php.maxChildren}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Avg Active
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {php.avgActiveProcesses}
                    </Typography>
                  </Box>
                  {php.maxChildrenReached > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Pool Exhausted
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="error.main"
                      >
                        {php.maxChildrenReached}x
                      </Typography>
                    </Box>
                  )}
                  {php.listenQueue > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Queue Length
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="warning.main"
                      >
                        {php.listenQueue}
                      </Typography>
                    </Box>
                  )}
                  {php.slowRequests > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Slow Requests
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="warning.main"
                      >
                        {php.slowRequests}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Top Slow Pages Table (only in session summary, not live) */}
      {!live && http?.topSlowPages?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Top Slow Pages
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell align="right">Avg Time</TableCell>
                  <TableCell align="right">Max Time</TableCell>
                  <TableCell align="right">Requests</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {http.topSlowPages.map((page, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {page.uri}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="caption"
                        color={
                          page.avgTime > 2000 ? "error.main" : "text.primary"
                        }
                      >
                        {page.avgTime}ms
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {page.maxTime}ms
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">{page.count}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

/**
 * Session detail view shown in expanded row
 */
const SessionDetailView = ({ sessionId, sessionDetail, loading }) => {
  if (loading || !sessionDetail || sessionDetail.session?.id !== sessionId) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const { session, dataPoints } = sessionDetail;
  const summary = session.summary;

  if (!summary) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">
          No summary data available for this session.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, px: 1 }}>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {Object.entries(summary.containers || {}).map(([container, stats]) => (
          <Grid item xs={12} md={6} key={container}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontFamily: "monospace", mb: 1 }}
                >
                  {container}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Speed sx={{ fontSize: 14, color: "primary.main" }} />
                      <Typography variant="caption" color="text.secondary">
                        CPU
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Avg: {stats.cpu?.avg?.toFixed(1)}% &middot; Max:{" "}
                      {stats.cpu?.max?.toFixed(1)}% &middot; P95:{" "}
                      {stats.cpu?.p95?.toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Memory sx={{ fontSize: 14, color: "secondary.main" }} />
                      <Typography variant="caption" color="text.secondary">
                        Memory
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Avg: {stats.memory?.avgPercent?.toFixed(1)}% &middot; Max:{" "}
                      {formatBytes(stats.memory?.maxUsageBytes)} /{" "}
                      {formatBytes(stats.memory?.limitBytes)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <NetworkCheck sx={{ fontSize: 14, color: "info.main" }} />
                      <Typography variant="caption" color="text.secondary">
                        Network
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      ↓ {formatBytes(stats.network?.totalRxBytes)} ↑{" "}
                      {formatBytes(stats.network?.totalTxBytes)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Storage sx={{ fontSize: 14, color: "warning.main" }} />
                      <Typography variant="caption" color="text.secondary">
                        Disk I/O
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Read: {formatBytes(stats.blockIo?.totalReadBytes)}{" "}
                      &middot; Write:{" "}
                      {formatBytes(stats.blockIo?.totalWriteBytes)}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {stats.samples} samples collected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Application-Level Metrics in Session Summary */}
      {summary.appMetrics && (
        <AppMetricsPanel appMetrics={summary.appMetrics} />
      )}

      {/* Overall Stats */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          color: "text.secondary",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Timer sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            Duration: {formatDuration(summary.durationMs)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Assessment sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            Total data points: {summary.totalDataPoints}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ApmTab;
